// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { InfoOutlined } from '@/constants/hardfin-standard-icons';
import {
  MembershipFacet,
  MembershipJoinRuleKind,
  MembershipLeaveRuleKind,
  MembershipScope,
  MembershipScopeOption,
} from '@/membership/api/membership-types';
import {
  Autocomplete,
  Checkbox,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { HTMLAttributes, Key, ReactNode, useMemo } from 'react';

// Past this the chips wrap into a block that pushes the rest of the dialog
// down. The rest collapse into a "+N" the field expands on focus.
const VISIBLE_TAGS = 3;

// The row that stands for every option, including the ones this project does
// not have yet. It is a picker construct and never reaches the definition.
const ALL_OPTION_KEY = '__all__';

// The order joinRules are stored in, so editing one does not reorder the
// definition and make an unchanged rule set look changed.
const JOIN_RULE_ORDER: MembershipJoinRuleKind[] = [
  'DELIVERED_TO_LOCATION',
  'FULFILLED_IN_WINDOW',
];

const LEFT_LOCATION: MembershipLeaveRuleKind = 'LEFT_PROJECT_LOCATION';

interface Props {
  scope: MembershipScope;
  items: MembershipScopeOption[];
  onChange: (next: MembershipScope) => void;
  // The report each section produces, shown under it. The pair is what makes
  // the effect of the leave rule visible.
  excludeReport?: ReactNode;
}

/**
 * What the membership report covers: which SKUs, how a unit joined the project,
 * and which of its days count once it has.
 *
 * A rule that is on asks for everything the project has. Narrowing one to named
 * locations was offered behind an Advanced link and is gone.
 */
export function MembershipRulesSection(props: Props) {
  const hasNoItems =
    !props.scope.items.isAll && props.scope.items.keys.length === 0;
  const hasNoJoinRule = props.scope.joinRules.length === 0;
  const leftLocation = props.scope.leaveRules.find(
    (rule) => rule.kind === LEFT_LOCATION
  );

  const toJoinRuleProps = (kind: MembershipJoinRuleKind) => ({
    isChecked: toFacet(props.scope, kind) !== undefined,
    onToggle: (isChecked: boolean) => {
      props.onChange(toScopeWithJoinRule(props.scope, kind, isChecked));
    },
  });

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2">Rules</Typography>
        <Tooltip
          arrow
          title="An asset starts being a member on the day that it joins the project, and stops being a member on the day that it leaves the project."
        >
          <InfoOutlined fontSize="small" color="action" />
        </Tooltip>
      </Stack>

      <Stack spacing={2}>
        <ScopePicker
          label="SKUs"
          allLabel="All SKUs"
          placeholder="Select at least one SKU"
          options={props.items}
          facet={props.scope.items}
          hasError={hasNoItems}
          helperText={hasNoItems ? 'Select at least one SKU' : undefined}
          onChange={(items) => {
            props.onChange({ ...props.scope, items });
          }}
        />

        <RuleGroup title="When assets join">
          <LabelledSwitch
            label="Fulfilled for this project"
            tooltip="When an asset is delivered on a fulfillment order linked to this project. Project membership takes effect on the delivery date."
            hasError={hasNoJoinRule}
            {...toJoinRuleProps('FULFILLED_IN_WINDOW')}
          />

          <LabelledSwitch
            label="Delivered to any project location during the term"
            tooltip="When an asset is delivered to any location associated with the project, between the project start and end dates. This includes manual movements which are not associated with fulfillment orders or shipments."
            hasError={hasNoJoinRule}
            {...toJoinRuleProps('DELIVERED_TO_LOCATION')}
          />

          {hasNoJoinRule && (
            <Typography variant="caption" color="error" sx={{ ml: 1.75 }}>
              Turn on at least one join rule
            </Typography>
          )}
        </RuleGroup>

        <RuleGroup title="When assets leave">
          <LabelledSwitch
            label="Moved away from a project location"
            tooltip="When an asset moves from a project location. The asset is no longer considered a member as of the date it leaves."
            isChecked={leftLocation !== undefined}
            hasError={false}
            onToggle={(isChecked) => {
              props.onChange({
                ...props.scope,
                leaveRules: isChecked
                  ? [{ kind: LEFT_LOCATION, facet: { isAll: true, keys: [] } }]
                  : [],
              });
            }}
          />
        </RuleGroup>

        {props.excludeReport}
      </Stack>
    </Paper>
  );
}

function toFacet(
  scope: MembershipScope,
  kind: MembershipJoinRuleKind
): MembershipFacet | undefined {
  return scope.joinRules.find((rule) => rule.kind === kind)?.facet;
}

/** Adds or removes one join rule, keeping the list in its canonical order. */
function toScopeWithJoinRule(
  scope: MembershipScope,
  kind: MembershipJoinRuleKind,
  isChecked: boolean
): MembershipScope {
  if (!isChecked) {
    return {
      ...scope,
      joinRules: scope.joinRules.filter((rule) => rule.kind !== kind),
    };
  }
  // Only the delivery rule takes a facet. Handing one to a rule that takes none
  // is rejected by the backend, so the whole rule set fails to save.
  return toScopeWithFacet(
    scope,
    kind,
    kind === 'DELIVERED_TO_LOCATION'
      ? { isAll: true, keys: [] }
      : { isAll: false, keys: [] }
  );
}

function toScopeWithFacet(
  scope: MembershipScope,
  kind: MembershipJoinRuleKind,
  facet: MembershipFacet
): MembershipScope {
  const rules = scope.joinRules
    .filter((rule) => rule.kind !== kind)
    .concat({ kind, facet });
  return {
    ...scope,
    joinRules: JOIN_RULE_ORDER.flatMap((candidate) =>
      rules.filter((rule) => rule.kind === candidate)
    ),
  };
}

interface RuleGroupProps {
  title: string;
  children: ReactNode;
}

function RuleGroup(props: RuleGroupProps) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {props.title}
      </Typography>
      <Stack spacing={0.5}>{props.children}</Stack>
    </Paper>
  );
}

interface LabelledSwitchProps {
  label: string;
  tooltip?: string;
  isChecked: boolean;
  hasError: boolean;
  onToggle: (isChecked: boolean) => void;
}

function LabelledSwitch(props: LabelledSwitchProps) {
  const control = (
    <FormControlLabel
      control={
        <Switch
          size="small"
          checked={props.isChecked}
          onChange={(event) => {
            props.onToggle(event.target.checked);
          }}
        />
      }
      label={
        <Typography
          variant="body2"
          color={props.hasError ? 'error' : undefined}
        >
          {props.label}
        </Typography>
      }
    />
  );

  if (props.tooltip === undefined) return control;

  // The icon sits beside the label rather than inside it. A tooltip on a child
  // of the label lends its whole text to the switch's accessible name, and a
  // click on it reaches the switch and flips the rule.
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      {control}
      <Tooltip arrow title={props.tooltip}>
        <InfoOutlined fontSize="small" color="action" />
      </Tooltip>
    </Stack>
  );
}

// What MUI actually hands renderOption, which carries a key its own types omit.
type OptionProps = HTMLAttributes<HTMLLIElement> & { key?: Key };

interface ScopePickerProps {
  label: string;
  // What the field reads when the facet is set to all, which is the default and
  // the case a list of chips describes worst.
  allLabel: string;
  placeholder: string;
  options: MembershipScopeOption[];
  facet: MembershipFacet;
  hasError: boolean;
  helperText?: string;
  onChange: (facet: MembershipFacet) => void;
}

function ScopePicker(props: ScopePickerProps) {
  const theme = useTheme();
  // The dialog owns the scrubbed day, so this re-renders on every tick of a
  // drag. Rebuilding the lists each time hands the autocomplete new arrays and
  // makes it redo its own work for a change that never touched the rules.
  const allLabel = props.allLabel;
  const options = useMemo(
    () => [
      { key: ALL_OPTION_KEY, label: allLabel } as MembershipScopeOption,
      ...props.options,
    ],
    [allLabel, props.options]
  );
  const facet = props.facet;
  const selected = useMemo(
    () =>
      facet.isAll
        ? options
        : props.options.filter((option) => facet.keys.includes(option.key)),
    [facet, options, props.options]
  );

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      size="small"
      limitTags={VISIBLE_TAGS}
      options={options}
      value={selected}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.key === value.key}
      // Every row is ticked while the facet is all, so the individual ones stop
      // taking clicks rather than silently doing nothing.
      getOptionDisabled={(option) =>
        props.facet.isAll && option.key !== ALL_OPTION_KEY
      }
      onChange={(_event, next, reason, details) => {
        if (details?.option.key === ALL_OPTION_KEY) {
          props.onChange({ isAll: reason === 'selectOption', keys: [] });
          return;
        }
        // Backspace on an empty input removes the last value, which while the
        // facet is all is a real SKU rather than the all row. Letting it
        // through would freeze the facet to the SKUs that exist today, which is
        // exactly what the mode exists to avoid.
        if (props.facet.isAll) return;
        props.onChange({
          isAll: false,
          keys: next
            .filter((option) => option.key !== ALL_OPTION_KEY)
            .map((option) => option.key),
        });
      }}
      renderOption={(optionProps, option, state) => {
        // MUI puts a key inside these props, and React ignores a key that
        // arrives through a spread, so it has to be taken out by hand.
        const rowProps: OptionProps = { ...optionProps };
        delete rowProps.key;
        return (
          // The rule under the all row is a border rather than a Divider
          // sibling, because a second node in the option array would need a key
          // of its own that MUI does not supply.
          <li
            key={option.key}
            {...rowProps}
            style={
              option.key === ALL_OPTION_KEY
                ? { borderBottom: `1px solid ${theme.palette.divider}` }
                : undefined
            }
          >
            <Checkbox size="small" checked={state.selected} sx={{ mr: 1 }} />
            {option.label}
          </li>
        );
      }}
      renderTags={(value, getTagProps) => {
        // One chip for the whole list beats a row of them saying the same.
        if (props.facet.isAll) {
          return <Chip size="small" label={props.allLabel} />;
        }
        return value.map((option, index) => (
          <Chip
            size="small"
            label={option.label}
            {...getTagProps({ index })}
            key={option.key}
          />
        ));
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={props.label}
          placeholder={selected.length === 0 ? props.placeholder : ''}
          error={props.hasError}
          helperText={props.helperText}
        />
      )}
    />
  );
}
